import * as React from "react";
import { Box, Typography, Stack, Chip, CircularProgress, IconButton, Button, Checkbox, FormControlLabel } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import type { SatRec } from "satellite.js";
import { fetchMyGroundStation, fetchMyGroupTle, fetchMyUplinkCommand, fetchMyUplinkStatus } from "../api/queries";
import { parseTle } from "./uplink/tle";
import { linkLock, usePassWindow, type GroundStation } from "./uplink/passWindow";
import { PAYLOAD_EFFECTS, formatDataSize, payloadResultFor, type PayloadResult } from "./uplink/payloadResults";
import { CheckRow, DataField, Led, MCS_COLORS, MONO, Panel, StatusLamp, formatClock, formatMissionTime } from "./uplink/mcsUi";
import PassPanel from "./uplink/PassPanel";
import TelemetryPanel, { type PayloadState } from "./uplink/TelemetryPanel";
import PayloadPanel from "./uplink/PayloadPanel";
import { getSession } from "../api/session";
import { RISK_STYLES } from "./utils/commandRisk";
import type { CommandRow } from "../types/commands";
import type { UplinkStatus } from "../types/workshop";

/* ------------------------------------------------------------------ *
 * The group's mission control screen (MCS). Everything here is a
 * client-side simulation: nothing is sent anywhere. It follows a classic
 * mission control layout (SCOS-2000 style): a status bar, the ground
 * station pass and link, the satellite's telemetry, the command chain
 * (definition -> pre-transmission validity -> manual stack release ->
 * staged verification, reported in the log), the payload data, and an
 * operations log -- all
 * on the simulated mission clock (UTC), which fast-forwards between
 * passes and runs in real time during one.
 * ------------------------------------------------------------------ */

type Phase = "idle" | "armed" | "transmitting" | "complete";
type LogLevel = "INFO" | "OK" | "WARN" | "TX";
type LogEntry = { id: number; time: string; level: LogLevel; text: string };

const PTV_CHECKS = ["Command definition valid", "Operator session authorised", "Spacecraft in nominal mode"];

const STAGES = [
  { label: "Released", detail: "Placed on the release queue", delay: 600 },
  { label: "Uplinked", detail: "Radiated to the spacecraft", delay: 1100 },
  { label: "Accepted", detail: "Onboard acceptance in telemetry", delay: 1300 },
  { label: "Executed", detail: "Execution started onboard", delay: 1300 },
  { label: "Completed", detail: "Execution confirmed in telemetry", delay: 1200 },
];

const PHASE_STYLES: Record<Phase, { label: string; color: string }> = {
  idle: { label: "IDLE", color: "#CBD5E1" },
  armed: { label: "ARMED", color: MCS_COLORS.warn },
  transmitting: { label: "TRANSMITTING", color: MCS_COLORS.info },
  complete: { label: "COMPLETE", color: MCS_COLORS.ok },
};

const LOG_COLORS: Record<LogLevel, string> = { INFO: "#A5B4FC", OK: MCS_COLORS.ok, WARN: MCS_COLORS.warn, TX: MCS_COLORS.info };

// Real seconds the downlink of the stored payload data takes.
const DOWNLINK_S = 12;

const disabledButtonSx = {
  "&.Mui-disabled": { bgcolor: "rgba(255,255,255,0.05)", color: "rgba(226,232,240,0.35)", border: "1px solid rgba(255,255,255,0.08)" },
};

function Shell(props: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        bgcolor: "#07040b",
        backgroundImage: "radial-gradient(circle at 12% 0%, rgba(124,58,237,0.16), transparent 45%)",
        px: { xs: 1.5, sm: 2.5, md: 3 },
        pt: { xs: 9, sm: 9, md: 7 },
        pb: { xs: 4, sm: 5 },
      }}
    >
      <Stack spacing={{ xs: 1.75, md: 2 }} sx={{ width: "100%", maxWidth: 1400 }}>
        {props.children}
      </Stack>
    </Box>
  );
}

function StatusCell(props: { label: string; value: React.ReactNode; color?: string; led?: string; pulsing?: boolean }) {
  return (
    <Stack spacing={0.3} sx={{ px: 1.5, py: 1, minWidth: 0, borderRight: "1px solid rgba(168,85,247,0.15)" }}>
      <Typography sx={{ fontFamily: MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.16em", color: MCS_COLORS.label }}>{props.label}</Typography>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        {props.led && <Led color={props.led} pulsing={props.pulsing} />}
        <Typography noWrap component="div" sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 700, color: props.color ?? "#FFFFFF" }}>
          {props.value}
        </Typography>
      </Stack>
    </Stack>
  );
}

export default function CommandExecutor(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const session = React.useMemo(() => getSession(), []);

  const [uplink, setUplink] = React.useState<UplinkStatus | null>(null);
  const [command, setCommand] = React.useState<CommandRow | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  // The group's orbit and the workshop's ground station: the command can only
  // go up while the satellite is in view (see uplink/passWindow.ts).
  const [satrec, setSatrec] = React.useState<SatRec | null>(null);
  const [tleLines, setTleLines] = React.useState<[string, string] | null>(null);
  const [station, setStation] = React.useState<GroundStation | null>(null);
  const pw = usePassWindow(satrec, station);
  const link = linkLock(pw);

  const [phase, setPhase] = React.useState<Phase>("idle");
  const [passedChecks, setPassedChecks] = React.useState(0);
  const [confirmed, setConfirmed] = React.useState(false);
  const [log, setLog] = React.useState<LogEntry[]>([]);

  // What the payload does after the command (see PayloadPanel).
  const [payloadState, setPayloadState] = React.useState<PayloadState>("off");
  const [recordWindow, setRecordWindow] = React.useState<{ start: number; end: number } | null>(null);
  const [downlink, setDownlink] = React.useState(0);

  const timers = React.useRef<number[]>([]);
  const logId = React.useRef(0);
  const logRef = React.useRef<HTMLDivElement | null>(null);
  // Log entries carry the simulated mission time, like the rest of the screen.
  const simNowRef = React.useRef(pw.simNow.getTime());
  simNowRef.current = pw.simNow.getTime();

  const clearTimers = React.useCallback(() => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  }, []);

  const schedule = React.useCallback((fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);

  const addLog = React.useCallback((level: LogLevel, text: string) => {
    logId.current += 1;
    const entry: LogEntry = { id: logId.current, time: formatClock(new Date(simNowRef.current)), level, text };
    setLog((prev) => [...prev, entry]);
  }, []);

  React.useEffect(() => clearTimers, [clearTimers]);

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      // Only for members who have submitted their group's plan and entered
      // the group's uplink code; it opens straight on the mission's command.
      const { data: status } = await fetchMyUplinkStatus();
      if (!mounted) return;
      if (!status?.uplink_unlocked) {
        handlePageChange(status?.plan_submitted ? "UplinkWaiting" : "MissionView");
        return;
      }
      setUplink(status);

      // No command without an orbit: the TLE comes from the group's launch.
      const [{ data: tle }, { data: gs }] = await Promise.all([fetchMyGroupTle(), fetchMyGroundStation()]);
      if (!mounted) return;
      const parsed = tle?.line1 && tle.line2 ? parseTle(tle.line1, tle.line2) : null;
      if (!parsed || !tle?.line1 || !tle.line2) {
        handlePageChange("Launch");
        return;
      }
      setSatrec(parsed);
      setTleLines([tle.line1, tle.line2]);
      if (gs && gs.lat !== null && gs.lon !== null) {
        setStation({ name: gs.name || "Ground station", latDeg: gs.lat, lonDeg: gs.lon, altM: gs.alt_m });
      }

      const { data, error } = await fetchMyUplinkCommand();
      if (!mounted) return;
      setLoading(false);
      if (error) {
        setLoadError(error.message);
        return;
      }
      setCommand(data ?? null);
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const satName = uplink ? `${uplink.group_name}-SAT` : "Satellite";
  const catalog = tleLines ? tleLines[0].slice(2, 7) : "";
  const orbitMinutes = tleLines ? 1440 / Number(tleLines[1].slice(52, 63)) : 95;
  const effect = command ? PAYLOAD_EFFECTS[command.command_code] : undefined;
  const result: PayloadResult | null = React.useMemo(
    () => (effect && catalog ? payloadResultFor(effect.kind, Number(catalog)) : null),
    [effect, catalog]
  );

  // (Re)initialise the executor whenever a command is loaded: reset state,
  // then walk through the pre-transmission validity checks.
  React.useEffect(() => {
    if (!command) return;
    clearTimers();
    logId.current = 0;
    setLog([]);
    setPhase("idle");
    setPassedChecks(0);
    setConfirmed(false);
    setPayloadState("off");

    addLog("INFO", `Command ${command.command_code} ${command.title} loaded`);
    addLog("INFO", `Operator ${session?.email ?? "unknown"} — running pre-transmission validity checks`);
    PTV_CHECKS.forEach((label, index) => {
      schedule(() => {
        setPassedChecks(index + 1);
        addLog("OK", `PTV: ${label}`);
      }, 450 * (index + 1));
    });
    schedule(() => addLog("INFO", "Pre-transmission validity checks complete"), 450 * PTV_CHECKS.length + 120);
  }, [command, session, addLog, schedule, clearTimers]);

  // Contact and link events in the log.
  const lastLink = React.useRef<{ contact: boolean; frame: boolean; tc: boolean } | null>(null);
  React.useEffect(() => {
    if (!command || !station) return;
    const prev = lastLink.current;
    if (prev) {
      if (!prev.contact && pw.inContact) addLog("OK", `AOS — ${satName} rises over ${station.name}`);
      if (!prev.frame && link.frameSync) addLog("OK", "Frame sync — telemetry is coming in");
      if (!prev.tc && link.commandLink) addLog("OK", "Command link ready — commanding possible");
      if (prev.contact && !pw.inContact) addLog("WARN", `LOS — ${satName} sets below the horizon of ${station.name}`);
    }
    lastLink.current = { contact: pw.inContact, frame: link.frameSync, tc: link.commandLink };
  }, [pw.inContact, link.frameSync, link.commandLink, command, station, satName, addLog]);

  // The payload after the command: on board until the satellite is over the
  // target area (after this pass), recording while out of contact, then
  // the stored data comes down at the next pass.
  const simMs = pw.simNow.getTime();
  React.useEffect(() => {
    if (!result || !effect) return;
    if (payloadState === "scheduled" && !pw.inContact && pw.waiting && pw.pass) {
      setRecordWindow({ start: simMs, end: pw.pass.aos.getTime() });
      setPayloadState("recording");
      addLog("INFO", `${satName} flies on towards ${result.areaName}; the ${effect.payloadName} works on board`);
    } else if (payloadState === "recording" && pw.inContact) {
      setPayloadState("stored");
      addLog("OK", `TM: ${formatDataSize(result.dataKb)} stored on board (${result.units} ${result.unitLabel})`);
    } else if (payloadState === "stored" && link.frameSync) {
      setPayloadState("downlinking");
      addLog("TX", `Downlink of the stored ${result.unitLabel} started`);
    }
  }, [result, effect, payloadState, pw.inContact, pw.waiting, pw.pass, link.frameSync, simMs, satName, addLog]);

  React.useEffect(() => {
    if (payloadState !== "downlinking" || !link.frameSync) return;
    const timer = window.setInterval(() => setDownlink((d) => Math.min(1, d + 0.25 / DOWNLINK_S)), 250);
    return () => window.clearInterval(timer);
  }, [payloadState, link.frameSync]);

  React.useEffect(() => {
    if (payloadState !== "downlinking" || downlink < 1 || !result) return;
    setPayloadState("received");
    if (result.kind === "ais") {
      const dark = result.data.vessels.filter((v) => v.dark).length;
      addLog("OK", `Downlink complete: ${result.data.vessels.length - dark} ships heard, ${dark} expected ships silent — possible dark ships`);
    } else {
      addLog(
        "OK",
        `Downlink complete: ${result.units} images of ${result.data.target.name} — front retreated ${result.data.retreatM} m since ${result.data.referenceYear}`
      );
    }
  }, [payloadState, downlink, result, addLog]);

  React.useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log]);

  const needsConfirm = command ? command.risk_level === "high" || command.risk_level === "critical" : false;
  const ptvComplete = passedChecks === PTV_CHECKS.length;
  const canArm = phase === "idle" && ptvComplete && (!needsConfirm || confirmed);
  const canTransmit = phase === "armed" && link.commandLink;

  const arm = () => {
    if (!command) return;
    setPhase("armed");
    addLog("WARN", `Release ARMED for ${command.command_code}`);
  };

  const disarm = () => {
    setPhase("idle");
    addLog("WARN", "Release disarmed by operator");
  };

  const reset = () => {
    setPhase("idle");
    setConfirmed(false);
    addLog("INFO", "Executor reset — ready for the next release");
  };

  const transmit = () => {
    if (!command) return;
    setPhase("transmitting");
    addLog("TX", `Release confirmed — transmitting ${command.command_code} to ${satName}`);

    let elapsed = 0;
    STAGES.forEach((stage, index) => {
      elapsed += stage.delay;
      schedule(() => {
        const last = index === STAGES.length - 1;
        addLog(last ? "OK" : "TX", `Verification: ${stage.label} — ${stage.detail}`);
        if (last) {
          setPhase("complete");
          addLog("OK", `${command.command_code} completed successfully`);
          if (effect) {
            setDownlink(0);
            setPayloadState("scheduled");
            addLog("OK", `TM: ${effect.payloadName} ON`);
          }
        }
      }, elapsed);
    });
  };

  const hint =
    phase === "complete"
      ? effect
        ? `Command executed. Watch the payload: its data comes down at the next pass.`
        : "Release complete. Reset to run it again."
      : phase === "transmitting"
        ? "Transmission in progress…"
        : phase === "armed" && !station
          ? "Armed, but the ground station isn't set up yet. Please ask our team."
          : phase === "armed" && !pw.inContact
            ? `Armed. Waiting for ${satName} to rise over ${station?.name ?? "the ground station"}…`
            : phase === "armed" && !link.commandLink
              ? "Armed. Waiting for the command link to lock…"
              : phase === "armed"
                ? "Armed and the command link is ready. Transmit now."
                : !ptvComplete
                  ? "Waiting for pre-transmission checks…"
                  : needsConfirm && !confirmed
                    ? "Confirm the release to enable arming."
                    : "Ready. Arm the release to continue.";

  const linkStatus = !station
    ? { label: "NO STATION", color: MCS_COLORS.alarm }
    : link.commandLink
      ? { label: "LOCKED", color: MCS_COLORS.ok }
      : pw.inContact
        ? { label: "ACQUIRING", color: MCS_COLORS.info }
        : { label: "NO LINK", color: MCS_COLORS.idle };

  const header = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <IconButton
        onClick={() => handlePageChange("Dashboard")}
        aria-label="Back to dashboard"
        sx={{
          color: "#E9D5FF",
          border: "1px solid rgba(168,85,247,0.35)",
          bgcolor: "rgba(255,255,255,0.04)",
          "&:hover": { bgcolor: "rgba(168,85,247,0.16)" },
        }}
      >
        <ArrowBackRoundedIcon />
      </IconButton>
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontFamily: MONO, color: "#C4B5FD", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", fontSize: { xs: 10, sm: 11 } }}
        >
          Command uplink{command ? ` / ${command.command_code}` : ""}
        </Typography>
        <Typography sx={{ color: "#FFFFFF", fontWeight: 900, fontSize: { xs: 22, sm: 28 } }}>Mission Control</Typography>
      </Stack>
      <Chip
        label="SIMULATION"
        size="small"
        sx={{
          ml: "auto !important",
          bgcolor: "rgba(250,204,21,0.12)",
          color: "#FDE68A",
          border: "1px solid rgba(250,204,21,0.4)",
          fontFamily: MONO,
          fontWeight: 800,
          fontSize: 11,
          letterSpacing: "0.08em",
        }}
      />
    </Stack>
  );

  if (loading || !command) {
    return (
      <Shell>
        {header}
        {loading ? (
          <Stack alignItems="center" sx={{ py: 10 }}>
            <CircularProgress sx={{ color: "#C4B5FD" }} />
          </Stack>
        ) : (
          <Typography sx={{ color: loadError ? MCS_COLORS.alarm : "rgba(226,232,240,0.7)" }}>
            {loadError ? `Failed to load the command: ${loadError}` : "Your mission's command isn't ready yet. Please ask our team."}
          </Typography>
        )}
      </Shell>
    );
  }

  const risk = RISK_STYLES[command.risk_level];
  const phaseStyle = PHASE_STYLES[phase];
  const checkState = (index: number): "pass" | "running" | "pending" =>
    index < passedChecks ? "pass" : index === passedChecks ? "running" : "pending";
  const recording = recordWindow ? Math.min(1, Math.max(0, (simMs - recordWindow.start) / (recordWindow.end - recordWindow.start))) : 0;
  const storedKb = !result
    ? 0
    : payloadState === "recording"
      ? Math.round(result.dataKb * recording)
      : payloadState === "stored"
        ? result.dataKb
        : payloadState === "downlinking"
          ? Math.round(result.dataKb * (1 - downlink))
          : 0;

  return (
    <Shell>
      {header}

      {/* Status bar */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(6, minmax(0, 1fr))" },
          borderRadius: 2,
          border: "1px solid rgba(168,85,247,0.3)",
          bgcolor: "rgba(9,5,11,0.85)",
          overflow: "hidden",
        }}
      >
        <StatusCell label="MISSION" value={uplink?.mission_name ?? "—"} />
        <StatusCell label="SPACECRAFT" value={`${satName}${catalog ? ` · ${catalog}` : ""}`} />
        <StatusCell label="GROUND STATION" value={station?.name ?? "not set"} color={station ? "#FFFFFF" : MCS_COLORS.alarm} />
        <StatusCell label="LINK" value={linkStatus.label} color={linkStatus.color} led={linkStatus.color} pulsing={pw.inContact && !link.commandLink} />
        <StatusCell
          label="CLOCK"
          value={pw.waiting ? `FAST-FWD ×${Math.round(pw.speed)}` : "REAL TIME"}
          color={pw.waiting ? MCS_COLORS.warn : MCS_COLORS.ok}
        />
        <StatusCell label="MISSION TIME (UTC, SIM)" value={formatMissionTime(pw.simNow)} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2.2fr) minmax(0, 1fr)" }, gap: 2, alignItems: "start" }}>
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <PassPanel station={station} pw={pw} canSkip={phase === "complete"} />
            <TelemetryPanel
              pw={pw}
              link={link}
              orbitMinutes={orbitMinutes}
              payloadName={effect?.payloadName ?? "Payload"}
              payloadState={payloadState}
              storedKb={storedKb}
            />
          </Box>

          <Panel title="Command Definition" dense>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "1.1fr 1.2fr 0.8fr 0.8fr 1.3fr" }, gap: 1.5, alignItems: "start" }}>
              <DataField label="Code" value={command.command_code} color="#C4B5FD" />
              <DataField label="Mnemonic" value={command.title} />
              <DataField label="Subsystem" value={command.category} />
              <Stack spacing={0.35}>
                <Typography sx={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", color: MCS_COLORS.label }}>
                  CRITICALITY
                </Typography>
                <Box>
                  <Chip
                    label={risk.label}
                    size="small"
                    sx={{ bgcolor: risk.bg, color: risk.color, border: `1px solid ${risk.border}`, fontWeight: 800, fontSize: 11, height: 22 }}
                  />
                </Box>
              </Stack>
              <DataField label="Operator" value={session?.email ?? "—"} />
            </Box>
            {command.description && (
              <Typography sx={{ mt: 1.25, color: "rgba(226,232,240,0.7)", fontSize: 13, lineHeight: 1.55 }}>{command.description}</Typography>
            )}
          </Panel>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <Panel
              title="Pre-Transmission Validity"
              right={<Typography sx={{ fontFamily: MONO, fontSize: 11, color: "rgba(226,232,240,0.6)" }}>{passedChecks}/{PTV_CHECKS.length}</Typography>}
            >
              <Stack spacing={1.25}>
                {PTV_CHECKS.map((label, index) => (
                  <CheckRow key={label} label={label} state={checkState(index)} />
                ))}
                <CheckRow label={link.commandLink ? "Command link locked" : "Command link (needs a pass)"} state={link.commandLink ? "pass" : "pending"} />
                <CheckRow
                  label={needsConfirm ? "Operator confirmation" : "Confirmation not required"}
                  state={ptvComplete && (!needsConfirm || confirmed) ? "pass" : "pending"}
                />
                {needsConfirm && (
                  <FormControlLabel
                    sx={{ mx: 0, mt: 0.5, alignItems: "flex-start" }}
                    control={
                      <Checkbox
                        checked={confirmed}
                        disabled={phase !== "idle" || !ptvComplete}
                        onChange={(e) => {
                          setConfirmed(e.target.checked);
                          addLog(e.target.checked ? "INFO" : "WARN", e.target.checked ? "Operator confirmed the release" : "Operator confirmation withdrawn");
                        }}
                        sx={{ p: 0.5, mr: 1, color: "rgba(196,181,253,0.6)", "&.Mui-checked": { color: "#A78BFA" }, "&.Mui-disabled": { color: "rgba(226,232,240,0.25)" } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 13, color: "rgba(226,232,240,0.8)", lineHeight: 1.5 }}>
                        I confirm this {risk.label.toLowerCase()}-criticality release is authorised.
                      </Typography>
                    }
                  />
                )}
              </Stack>
            </Panel>

            <Panel title="Manual Stack · Release" right={<StatusLamp color={phaseStyle.color} label={phaseStyle.label} pulsing={phase === "armed" || phase === "transmitting"} />}>
              <Stack spacing={1.25}>
                <Button
                  fullWidth
                  startIcon={<LockRoundedIcon />}
                  disabled={!canArm}
                  onClick={arm}
                  sx={{ textTransform: "none", fontWeight: 800, borderRadius: 1.5, py: 1, color: "#FFFFFF", bgcolor: "#7C3AED", "&:hover": { bgcolor: "#6D28D9" }, ...disabledButtonSx }}
                >
                  Arm release
                </Button>
                <Button
                  fullWidth
                  startIcon={<SendRoundedIcon />}
                  disabled={!canTransmit}
                  onClick={transmit}
                  sx={{ textTransform: "none", fontWeight: 800, borderRadius: 1.5, py: 1, color: "#FFFFFF", bgcolor: "#0891B2", "&:hover": { bgcolor: "#0E7490" }, ...disabledButtonSx }}
                >
                  Transmit
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={phase === "complete" ? <ReplayRoundedIcon /> : <CloseRoundedIcon />}
                  disabled={phase === "idle" || phase === "transmitting"}
                  onClick={phase === "complete" ? reset : disarm}
                  sx={{
                    textTransform: "none",
                    fontWeight: 800,
                    borderRadius: 1.5,
                    color: "#E9D5FF",
                    borderColor: "rgba(168,85,247,0.4)",
                    "&:hover": { borderColor: "rgba(168,85,247,0.7)", bgcolor: "rgba(168,85,247,0.12)" },
                    "&.Mui-disabled": { color: "rgba(226,232,240,0.3)", borderColor: "rgba(255,255,255,0.08)" },
                  }}
                >
                  {phase === "complete" ? "Reset" : "Disarm"}
                </Button>
                <Typography sx={{ fontFamily: MONO, fontSize: 11.5, color: "rgba(226,232,240,0.6)", lineHeight: 1.5 }}>{hint}</Typography>
              </Stack>
            </Panel>
          </Box>

          <PayloadPanel
            payloadName={effect?.payloadName ?? "Payload"}
            state={payloadState}
            result={result}
            recording={recording}
            downlink={downlink}
            inContact={pw.inContact}
          />
        </Stack>

        <Stack spacing={2} sx={{ minWidth: 0, position: { lg: "sticky" }, top: { lg: 16 } }}>
          <Panel
            title="Operations Log"
            right={<Typography sx={{ fontFamily: MONO, fontSize: 11, color: "rgba(226,232,240,0.6)" }}>{log.length} events · UTC (sim)</Typography>}
          >
            <Box
              ref={logRef}
              sx={{
                height: { xs: 280, lg: 520 },
                overflowY: "auto",
                p: 1.25,
                borderRadius: 1.5,
                border: "1px solid rgba(255,255,255,0.08)",
                bgcolor: "rgba(3,2,6,0.6)",
                fontFamily: MONO,
                fontSize: 11.5,
                lineHeight: 1.7,
              }}
            >
              {log.map((entry) => (
                <Box key={entry.id} sx={{ display: "flex", gap: 1.25, alignItems: "baseline" }}>
                  <Box component="span" sx={{ color: "rgba(148,163,184,0.7)", flexShrink: 0 }}>
                    {entry.time}
                  </Box>
                  <Box component="span" sx={{ color: LOG_COLORS[entry.level], fontWeight: 800, width: 34, flexShrink: 0 }}>
                    {entry.level}
                  </Box>
                  <Box component="span" sx={{ color: "rgba(226,232,240,0.88)", wordBreak: "break-word" }}>
                    {entry.text}
                  </Box>
                </Box>
              ))}
            </Box>
          </Panel>

          {tleLines && (
            <Panel title="Orbit · TLE" dense>
              <Box sx={{ overflowX: "auto" }}>
                <Typography component="pre" sx={{ m: 0, fontFamily: MONO, fontSize: 10.5, color: "rgba(233,213,255,0.85)", lineHeight: 1.6 }}>
                  {tleLines[0]}
                  {"\n"}
                  {tleLines[1]}
                </Typography>
              </Box>
              <Typography sx={{ mt: 1, fontFamily: MONO, fontSize: 10.5, color: "rgba(226,232,240,0.5)" }}>
                One orbit every {orbitMinutes.toFixed(1)} min · {Number(tleLines[1].slice(8, 16)).toFixed(1)}° inclination
              </Typography>
            </Panel>
          )}
        </Stack>
      </Box>
    </Shell>
  );
}
